import { useState } from "react";
import { ListGroup, Button, Form, Spinner, Dropdown } from "react-bootstrap";
import Message from "./Message";
import Loader from "./Loader";
import { toast } from "react-toastify";
import {
  useGetSourcingPartnersQuery,
} from "../slices/usersApiSlice";
import {
  useAssignSourcingPartnerMutation,
  useUpdateOrderCollectionMutation,
} from "../slices/ordersApiSlice";
import {
  useSendSourceOrderWhatsAppMutation
} from "../slices/whatsAppApiSlice";

const AssignSourcingPartner = ({ order, orderId, refetch, userInfo }) => {
  const { data: sourcingPartners = [], isLoading: isLoadingSourcingPartners, error: errorSourcingPartners } =
    useGetSourcingPartnersQuery("approved");

  const [assignSourcingPartner, { isLoading: isLoadingAssignSourcingPartner }] =
    useAssignSourcingPartnerMutation();

  const [sendSourceOrderWhatsApp, { isLoading: isLoadingSendWhatsApp }] =
    useSendSourceOrderWhatsAppMutation();

  const [updateOrderCollection, { isLoading: isLoadingOrderCollection }] =
    useUpdateOrderCollectionMutation();

  const [selectedSourcingPartner, setSelectedSourcingPartner] = useState(
    order?.sourcing?.sourcingPartner?._id || null
  );
  const [selectedSourcingPartnerAddress, setSelectedSourcingPartnerAddress] =
    useState(order?.sourcing?.sourcingPartnerAddress?._id || null);

  const handleAssignSourcingPartner = async () => {
    try {
      if (!selectedSourcingPartner && !selectedSourcingPartnerAddress) {
        toast.error("Please select a delivery partner", { autoClose: 2000 });
        return;
      }

      await assignSourcingPartner({
        orderId,
        sourcingPartnerUserId: selectedSourcingPartner,
        sourcingPartnerAddressId: selectedSourcingPartnerAddress,
      }).unwrap();

      await refetch();
      setSelectedSourcingPartner(null);
      toast.success("Sourcing partner assigned", { autoClose: 1000 });
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const sendSourcingWhatsAppHandler = async () => {
    try {
      await sendSourceOrderWhatsApp({ orderId }).unwrap();
      toast.success("Sourcing partner notified on WhatsApp", { autoClose: 1000 });
    } catch (err) {
      toast.error(
        err?.data?.message || "Failed to send sourcing WhatsApp message",
        { autoClose: 2000 }
      );
    }
  };

  const updateOrderCollectionHandler = async () => {
    try {
      await updateOrderCollection(orderId);
      refetch();
      toast.success("Order marked as collected", { autoClose: 1500 });
    } catch (error) {
      toast.error(error?.data?.message || error.message);
    }
  };

  return (
    <div className="list-group border rounded p-1 mt-3">
      <ListGroup variant="flush">
        <ListGroup.Item>
          <h2>Sourcing Partner</h2>

          {(order?.sourcing?.sourcingPartner && order?.sourcing?.sourcingPartnerAddress) ? (
            <Message variant="info">
              Assigned to:{" "}
              <strong>
                {order.sourcing.sourcingPartner.name} ({order.sourcing.sourcingPartner.phone})
                <br />
              </strong>
              {order.sourcing.sourcingPartner.sourcingPartner.organizationName}
              <br />
              {order.sourcing.sourcingPartnerAddress.addressLine1}
              {order.sourcing.sourcingPartnerAddress.addressLine2 && <br />}
              {order.sourcing.sourcingPartnerAddress.addressLine2}
              <br />
              {order.sourcing.sourcingPartnerAddress.city} -{" "}
              {order.sourcing.sourcingPartnerAddress.city},{" "}
              {order.sourcing.sourcingPartnerAddress.state}
            </Message>
          ) : (
            <Message variant="warning">No sourcing partner assigned</Message>
          )}

          {!order?.sourcing?.isCollected && !order?.delivery?.isDelivered && (
            <>
              <hr />
              <h5>Assign Sourcing Partner</h5>

              {isLoadingSourcingPartners ? (
                <Loader />
              ) : errorSourcingPartners ? (
                <Message variant="danger">
                  {errorSourcingPartners?.data?.message || errorSourcingPartners.error}
                </Message>
              ) : (
                <>
                  <Dropdown className="my-2">
                    <Dropdown.Toggle
                      variant="light"
                      className="w-100 d-flex justify-content-between align-items-center border"
                    >
                      {selectedSourcingPartner ? (
                        <div className="text-wrap text-start flex-grow-1">
                          <strong>Selected:</strong>
                          <br />
                          {
                            sourcingPartners.find(
                              (p) => p._id === selectedSourcingPartner
                            )?.name
                          }{" "}
                          (
                          {
                            sourcingPartners.find(
                              (p) => p._id === selectedSourcingPartner
                            )?.phone
                          }
                          )
                          <br />
                          {
                            sourcingPartners.find(
                              (p) => p._id === selectedSourcingPartner
                            )?.sourcingPartner.organizationName
                          }
                        </div>
                      ) : (
                        "Select Sourcing Partner"
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="w-100">
                      {sourcingPartners.map((partner) => (
                        <Dropdown.Item as="div" key={partner._id}>
                          <Form.Check
                            type="radio"
                            name="sourcingPartner"
                            id={partner._id}
                            label={
                              <div className="text-wrap">
                                {partner.name} ({partner.phone}) <br />
                                {partner.sourcingPartner.organizationName}
                              </div>
                            }
                            checked={selectedSourcingPartner === partner._id}
                            onChange={() => {
                              setSelectedSourcingPartner(partner._id);
                              setSelectedSourcingPartnerAddress(
                                partner.sourcingPartner?.sourcingPartnerAddress?._id
                              );
                            }}
                          />
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  <Button
                    className="mt-2"
                    onClick={handleAssignSourcingPartner}
                    disabled={isLoadingAssignSourcingPartner || !selectedSourcingPartner}
                  >
                    {isLoadingAssignSourcingPartner ? (
                      <>
                        <Spinner animation="border" size="sm" /> Assigning...
                      </>
                    ) : (
                      "Assign Sourcing Partner"
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </ListGroup.Item>

        <ListGroup.Item>
        {userInfo?.isAdmin &&
          order?.sourcing?.sourcingPartner &&
          !order.sourcing.isCollected &&
          !order.delivery.isDelivered && (
            <>
              <h5>Notify Sourcing Partner</h5>

              <p className="text-muted small mb-2">
                Send order details and pickup time to the assigned sourcing partner via WhatsApp.
              </p>

              <Button
                onClick={sendSourcingWhatsAppHandler}
                disabled={
                  isLoadingSendWhatsApp ||
                  !order?.delivery?.preferredDeliveryDate ||
                  !order?.delivery?.preferredDeliveryTimeSlot
                }
              >
                {isLoadingSendWhatsApp ? (
                  <>
                    <Spinner animation="border" size="sm" /> Sending…
                  </>
                ) : (
                  "Send Order Sourcing Message"
                )}
              </Button>

              {(!order?.delivery?.preferredDeliveryDate ||
                !order?.delivery?.preferredDeliveryTimeSlot) && (
                <div className="text-danger small mt-2">
                  Pickup time slot must be finalized before sending.
                </div>
              )}
            </>
          )}
        </ListGroup.Item>

        <ListGroup.Item>
          {userInfo?.isAdmin &&
            order.isPaid &&
            !order.sourcing.isCollected &&
            !order.delivery.isDelivered && (
              <>
                <h5>Order Collection</h5>
                <Button onClick={updateOrderCollectionHandler}>
                  Mark order as collected
                </Button>
              </>
            )}
          {isLoadingOrderCollection && <Loader />}
        </ListGroup.Item>
      </ListGroup>
    </div>
  );
};

export default AssignSourcingPartner;